import { UpdateJobPermissions } from './UpdateJobPermissions.js';
import { getJobPermissionsEntry } from '../../features/job-permissions/job-permissions-service.js';

/**
 * Retrieves the sharing configuration of a project folder from Firebase,
 * walking up the folder hierarchy until reaching the `company_space` root.
 *
 * @async
 * @function GetFolderSharing
 * @param {string} folderPath - The already encoded path to folder under the `company_space/${companyId}/project_folders`.
 * @param {string} userGroup - The user group associated with the folder.
 * @param {Object} [options={}] - Optional configuration.
 * @param {Object} [options.firebase] - Custom Firebase reference; defaults to `self.FirebaseWorker` if not provided.
 * @returns {Promise<Object>} Resolves to an object containing the combined sharing settings of the folder and its parents.
 *
 * @throws {Error} If the `path` is not a valid string.
 */
export async function GetFolderSharing(folderPath, userGroup, options) {
  options = options || {};
  let firebase = options?.firebase || self.FirebaseWorker;
  // Make sure the project folder is a string
  if (typeof folderPath !== 'string') throw new Error(`Invalid project folder: ${folderPath}`);
  if (!userGroup) throw new Error(`Missing user group`);
  // Fetch the sharing object from Firebase
  let sharingObject = {};
  const pathComponents = folderPath.split('/');
  let lastPathComponent = pathComponents.at(-1);
  while (lastPathComponent && lastPathComponent != 'company_space') {
    const pathToCheck = `photoheight/company_space/${userGroup}/project_folders/${pathComponents.join('/')}`;
    const sharingData = await firebase
      .database()
      .ref(`${pathToCheck}/sharing`)
      .once('value')
      .then((s) => s.val());
    if (sharingData) {
      // If the sharing data exists, merge it into the sharingObject
      Object.assign(sharingObject, sharingData);
    }
    pathComponents.pop();
    // Remove the 'folders' component if present to move up the hierarchy
    if (pathComponents.at(-1) == 'folders') pathComponents.pop();
    lastPathComponent = pathComponents.at(-1);
  }
  return sharingObject;
}

/**
 * Updates the sharing information for a job when a folder is moved or updated.
 *
 * @async
 * @param {string} jobId - The ID of the job to update sharing for.
 * @param {string} userGroup - The user group associated with the sharing update.
 * @param {string} newEncodedPath - The encoded path of the new folder location.
 * @param {string} oldEncodedPath - The encoded path of the old folder location.
 * @param {Object} [options={}] - Optional configuration.
 * @returns {Promise<Object>} An object representing the updates to be applied to the database.
 */
export async function GetJobSharingUpdate(jobId, userGroup, newEncodedPath, oldEncodedPath, options) {
  options = options || {};
  let firebase = options?.firebase || self.FirebaseWorker;
  let update = {};

  let jobSharing = await firebase
    .database()
    .ref(`photoheight/jobs/${jobId}/sharing`)
    .once('value')
    .then((s) => s.val());
  const newFolderSharing = await GetFolderSharing(newEncodedPath, userGroup, options);
  const oldFolderSharing = await GetFolderSharing(oldEncodedPath, userGroup, options);

  for (const companyId in newFolderSharing) {
    const jobSharingPermission = jobSharing?.[companyId];
    // If the company is not in the current job sharing, share the job with the company as a new share
    if (!jobSharingPermission) {
      await shareNewJobWithCompany(jobId, companyId, newFolderSharing[companyId], userGroup, newEncodedPath, update, options);
      continue;
    }
    // If the company has a different permission than that of the new folder, update the job permission
    if (newFolderSharing[companyId] != jobSharingPermission) {
      await changeJobPermissionForCompany(jobId, companyId, newFolderSharing[companyId], userGroup, update, options);
      continue;
    }
  }
  for (const companyId in oldFolderSharing) {
    const newFolderPermission = newFolderSharing?.[companyId];
    // If the company is not in the new folder sharing, remove the job permission for the company
    if (newFolderPermission == null) {
      await removeJobPermissionForCompany(jobId, companyId, userGroup, oldEncodedPath, update, options);
    }
  }
  return update;
}

async function shareNewJobWithCompany(jobId, companyId, newPermission, userGroup, newEncodedPath, update, options) {
  options = options || {};
  let firebase = options?.firebase || self.FirebaseWorker;
  const jobCreator = (await firebase.database().ref(`photoheight/jobs/${jobId}/job_creator`).once('value')).val();
  const permissionsData = await getJobPermissionsEntry(userGroup, jobId, { firebase });

  update[`jobs/${jobId}/sharing/${companyId}`] = newPermission;
  update[`company_space/${jobCreator}/companies_with_model_access/${companyId}`] = true;
  update[`company_space/${companyId}/project_folders/${newEncodedPath}/jobs/${jobId}`] = true;
  permissionsData.date_shared = Date.now();
  permissionsData.permission = newPermission;
  permissionsData.status = 'active';
  permissionsData.done = true;
  delete permissionsData.archived_at;
  delete permissionsData.archived_by;

  // Set the job permission data for just the company we're sharing with
  await UpdateJobPermissions(jobId, userGroup, permissionsData, {
    updatePath: 'photoheight',
    skipAddingMainCompanyToSharing: true,
    sharing: { [companyId]: true },
    wipeExistingData: true,
    update,
    firebase
  });
}

async function changeJobPermissionForCompany(jobId, companyId, permission, userGroup, update, options) {
  options = options || {};
  let firebase = options?.firebase || self.FirebaseWorker;
  update[`jobs/${jobId}/sharing/${companyId}`] = permission;
  await UpdateJobPermissions(
    jobId,
    userGroup,
    { permission },
    {
      updatePath: 'photoheight',
      skipAddingMainCompanyToSharing: true,
      sharing: { [companyId]: true },
      update,
      firebase
    }
  );
}

async function removeJobPermissionForCompany(jobId, companyId, userGroup, oldEncodedPath, update, options) {
  options = options || {};
  let firebase = options?.firebase || self.FirebaseWorker;
  await UpdateJobPermissions(jobId, userGroup, null, {
    updatePath: 'photoheight',
    skipAddingMainCompanyToSharing: true,
    sharing: { [companyId]: true },
    update,
    firebase
  });
  update[`jobs/${jobId}/sharing/${companyId}`] = null;
  update[`company_space/${companyId}/project_folders/${oldEncodedPath}/jobs/${jobId}`] = null;
}
